import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, projectAPI, issuesAPI, feedbackAPI } from '../services/api';
import { Project } from '../types';

const CountyOfficerPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('budget-upload');
  const [projects, setProjects] = useState<Project[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [feedback, setFeedback] = useState<any[]>([]);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [editingDoc, setEditingDoc] = useState<any>(null);
  const [newProject, setNewProject] = useState({
    name: '',
    ward_id: '',
    sector: 'Infrastructure',
    budget: 0,
    contractor: '',
    start_date: '',
    expected_completion: '',
    description: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const hasAccess = user && user.role === 'county_officer';

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [activeTab, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'ward-submissions') {
        const projectsRes = await adminAPI.getPendingProjects();
        setProjects(projectsRes.data.filter((p: Project) => p.workflow_stage === 'ward_submitted'));
      } else if (activeTab === 'document-management') {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const token = localStorage.getItem('token') || '';
        // Try admin endpoint first, fall back to public documents
        try {
          const response = await fetch(`${API_BASE}/admin/documents/all`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setDocuments(Array.isArray(data) ? data : []);
          } else {
            // Fallback: use public documents endpoint and filter out engagements
            const fallback = await fetch(`${API_BASE}/documents`);
            const fallbackData = await fallback.json();
            setDocuments(Array.isArray(fallbackData)
              ? fallbackData.filter((d: any) => !d.id.startsWith('engagement-') && d.document_category !== 'engagement')
              : []);
          }
        } catch {
          setDocuments([]);
        }
      } else if (activeTab === 'feedback-review') {
        const res = await feedbackAPI.getAdmin();
        setFeedback(res.data || []);
      } else if (activeTab === 'ward-approvals') {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE}/county/documents/pending`);
        const data = await response.json();
        setPendingDocuments(Array.isArray(data) ? data : []);
      } else if (activeTab === 'county-issues') {
        const issuesRes = await issuesAPI.getAll();
        setIssues(issuesRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const uploadBudget = async (budgetData: any) => {
    try {
      await adminAPI.uploadBudget(budgetData);
      setMessage('Budget document uploaded for manager approval');
    } catch (error) {
      setMessage('Failed to upload budget document');
    }
  };

  const createProject = async () => {
    try {
      await projectAPI.create(newProject as any);
      setMessage('Project allocation created and submitted for manager approval');
      setNewProject({
        name: '',
        ward_id: '',
        sector: 'Infrastructure',
        budget: 0,
        contractor: '',
        start_date: '',
        expected_completion: '',
        description: ''
      });
      loadData();
    } catch (error) {
      setMessage('Failed to create project allocation');
    }
  };

  const validateProject = async (projectId: string, action: string, notes?: string) => {
    try {
      await adminAPI.approveProject(projectId, { action, notes });
      setMessage(`Project ${action} successfully`);
      loadData();
    } catch (error) {
      setMessage(`Failed to ${action} project`);
    }
  };

  const deleteDocument = async (docId: string) => {
    if (!window.confirm('Delete this document? Budget documents will reset all ward budgets and projects.')) return;
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token') || '';
      const response = await fetch(`${API_BASE}/admin/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setMessage(data.message || 'Document deleted successfully');
        loadData();
      } else {
        setMessage(data.error || `Delete failed (${response.status})`);
      }
    } catch (error) {
      setMessage('Failed to delete document');
    }
  };

  const updateDocument = async (docId: string, updates: any) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/admin/documents/${docId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        setMessage('Document updated successfully');
        setEditingDoc(null);
        loadData();
      } else {
        setMessage('Failed to update document');
      }
    } catch (error) {
      console.error('Update error:', error);
      setMessage('Failed to update document');
    }
  };

  const approveWardDocument = async (docId: string, action: 'approve' | 'reject', reason?: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/county/documents/${docId}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
        },
        body: JSON.stringify({
          action,
          approved_by: user.id,
          reason: reason || ''
        })
      });
      
      if (response.ok) {
        setMessage(`Document ${action}d successfully`);
        loadData();
      } else {
        setMessage(`Failed to ${action} document`);
      }
    } catch (error) {
      console.error('Approval error:', error);
      setMessage(`Failed to ${action} document`);
    }
  };

  if (!hasAccess) {
    return (
      <div className="county-officer-page">
        <header className="page-header">
          <div className="icon icon-primary">🔒</div>
          <h1>Institutional Responder Access Required</h1>
          <p>Only Institutional Responders can access this page</p>
        </header>
        <div className="access-denied">
          <p>This page is restricted to Institutional Responders only.</p>
          <button onClick={() => navigate('/login')} className="btn btn-primary">Staff Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="county-officer-page">
      <header className="page-header">
        <div className="icon icon-primary">🏛️</div>
        <h1>Institutional Responder Dashboard</h1>
        <p>Manage county-level budget data and documents</p>
      </header>

      {message && (
        <div className={`message ${message.includes('successfully') || message.includes('uploaded') || message.includes('created') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="county-officer-tabs">
        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'budget-upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('budget-upload')}
          >
            📄 Budget Upload
          </button>
          <button 
            className={`tab-button ${activeTab === 'project-allocations' ? 'active' : ''}`}
            onClick={() => setActiveTab('project-allocations')}
          >
            🏗️ Project Allocations
          </button>
          <button 
            className={`tab-button ${activeTab === 'ward-submissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('ward-submissions')}
          >
            📋 Ward Submissions
          </button>
          <button 
            className={`tab-button ${activeTab === 'county-issues' ? 'active' : ''}`}
            onClick={() => setActiveTab('county-issues')}
          >
            🚨 County Issues
          </button>
          <button 
            className={`tab-button ${activeTab === 'community-engagement' ? 'active' : ''}`}
            onClick={() => setActiveTab('community-engagement')}
          >
            🤝 Community Engagement
          </button>
          <button 
            className={`tab-button ${activeTab === 'volunteer-activities' ? 'active' : ''}`}
            onClick={() => setActiveTab('volunteer-activities')}
          >
            👥 Volunteer Activities
          </button>
          <button 
            className={`tab-button ${activeTab === 'document-management' ? 'active' : ''}`}
            onClick={() => setActiveTab('document-management')}
          >
            📁 Manage Documents
          </button>
          <button
            className={`tab-button ${activeTab === 'feedback-review' ? 'active' : ''}`}
            onClick={() => setActiveTab('feedback-review')}
          >
            📝 Feedback Review
          </button>
          <button
            className={`tab-button ${activeTab === 'ward-approvals' ? 'active' : ''}`}
            onClick={() => setActiveTab('ward-approvals')}
          >
            ✅ Ward Approvals
          </button>
        </div>
      </div>

      <div className="county-officer-content">
        {activeTab === 'budget-upload' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📄</div>
              <div>
                <h2>Upload Official Budget Documents</h2>
                <p>Upload official county budget documents and supporting materials for manager approval.</p>
              </div>
            </div>
            
            <div className="upload-forms">
              <div className="budget-upload-form">
                <h3>County Budget Document</h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  
                  // Add user information
                  formData.append('uploaded_by', user?.id || '');
                  
                  // Check if it's an Excel file for budget parsing
                  const fileInput = (e.target as HTMLFormElement).querySelector('input[name="file"]') as HTMLInputElement;
                  const file = fileInput?.files?.[0];
                  const isExcelFile = file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'));
                  const docType = formData.get('document_type');
                  
                  // Use Excel parsing endpoint for Excel budget files
                  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                  const endpoint = (isExcelFile && docType === 'budget') ? `${API_BASE}/admin/documents/excel` : `${API_BASE}/admin/documents`;
                  
                  // Submit budget document with file
                  setUploading(true);
                  setMessage('⏳ Uploading, please wait...');
                  fetch(endpoint, {
                    method: 'POST',
                    body: formData,
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
                    }
                  })
                  .then(response => {
                    if (!response.ok) {
                      return response.json().then(data => {
                        throw new Error(data.error || `HTTP error! status: ${response.status}`);
                      });
                    }
                    return response.text().then(text => {
                      try {
                        return JSON.parse(text);
                      } catch (e) {
                        console.error('Invalid JSON response:', text);
                        throw new Error('Invalid server response');
                      }
                    });
                  })
                  .then(data => {
                    if (data.message) {
                      let message = 'Budget document uploaded successfully';
                      if (data.ward_budgets_processed && data.ward_budgets_processed > 0) {
                        message += `. Updated ${data.wards_updated || data.ward_budgets_processed} ward budgets`;
                      }
                      if (data.projects_processed && data.projects_processed > 0) {
                        message += `. Created ${data.projects_processed} projects`;
                      }
                      if (data.total_budget_extracted) {
                        message += `. Total budget: KES ${(data.total_budget_extracted / 1e9).toFixed(2)}B`;
                      }
                      setMessage(message);
                      (e.target as HTMLFormElement).reset();
                    } else {
                      setMessage('Failed to upload budget document');
                    }
                  })
                  .catch(error => {
                    console.error('Upload error:', error);
                    setMessage(`Failed to upload budget document: ${error.message}`);
                  })
                  .finally(() => setUploading(false));
                }}>
                  <div className="form-row">
                    <input name="title" placeholder="Budget Document Title" defaultValue="Turkana County Budget FY 2025/2026" required />
                    <input name="county" placeholder="County" defaultValue={user?.county || 'Turkana'} required />
                  </div>
                  <div className="form-row">
                    <select name="document_type" required>
                      <option value="budget">Budget Document</option>
                    </select>
                    <input name="sector" placeholder="Sector (All)" defaultValue="All" />
                  </div>
                  <div className="form-row">
                    <textarea name="description" placeholder="Document Description" rows={2} />
                  </div>
                  
                  <div className="file-upload-section">
                    <h4>Upload Budget Document</h4>
                    <div className="upload-types">
                      <div className="upload-type">
                        <label>📊 Budget Document (Excel or PDF)</label>
                        <input name="file" type="file" accept=".pdf,.xlsx,.xls" required />
                        <small>Supported formats: PDF, Excel (.xlsx, .xls) scroll down to for the upload button</small>
                      </div>
                    </div>
                  </div>
                  
                  <h4>Sector Allocations (KES)</h4>
                  <div className="form-row">
                    <input name="health" type="number" placeholder="Health (Optional)" />
                    <input name="education" type="number" placeholder="Education (Optional)" />
                  </div>
                  <div className="form-row">
                    <input name="infrastructure" type="number" placeholder="Infrastructure (Optional)" />
                    <input name="water" type="number" placeholder="Water (Optional)" />
                  </div>
                  <div className="form-row">
                    <input name="agriculture" type="number" placeholder="Agriculture (Optional)" />
                    <input name="energy" type="number" placeholder="Energy (Optional)" />
                  </div>
                  <button type="submit" disabled={uploading}>{uploading ? '⏳ Uploading...' : 'Upload Budget Document'}</button>
                </form>
              </div>

              <div className="supporting-docs">
                <h3>Policy Documents</h3>
                <div className="doc-types">
                  <div className="doc-type">
                    <h4>County Integrated Development Plan (CIDP)</h4>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target as HTMLFormElement);
                      formData.append('title', 'County Integrated Development Plan (CIDP)');
                      formData.append('document_type', 'plan');
                      formData.append('uploaded_by', user?.id || '');
                      
                      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                      
                      fetch(`${API_BASE}/admin/documents`, {
                        method: 'POST',
                        body: formData,
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
                        }
                      })
                      .then(response => {
                        if (!response.ok) {
                          throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.text().then(text => {
                          try {
                            return JSON.parse(text);
                          } catch (e) {
                            console.error('Invalid JSON response:', text);
                            throw new Error('Invalid server response');
                          }
                        });
                      })
                      .then(data => {
                        if (data.message) {
                          setMessage('CIDP document uploaded successfully');
                          (e.target as HTMLFormElement).reset();
                        } else {
                          setMessage('Failed to upload CIDP document');
                        }
                      })
                      .catch(error => {
                        console.error('Upload error:', error);
                        setMessage(`Failed to upload CIDP document: ${error.message}`);
                      });
                    }}>
                      <input name="file" type="file" accept=".pdf,.doc,.docx" required />
                      <textarea name="description" placeholder="CIDP Description" rows={2} />
                      <button type="submit" className="btn btn-secondary" disabled={uploading}>{uploading ? '⏳ Uploading...' : 'Upload CIDP'}</button>
                    </form>
                  </div>
                  <div className="doc-type">
                    <h4>Annual Development Plan (ADP)</h4>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target as HTMLFormElement);
                      formData.append('title', 'Annual Development Plan (ADP)');
                      formData.append('document_type', 'plan');
                      formData.append('uploaded_by', user?.id || '');
                      
                      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                      
                      fetch(`${API_BASE}/admin/documents`, {
                        method: 'POST',
                        body: formData,
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
                        }
                      })
                      .then(response => {
                        if (!response.ok) {
                          throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.text().then(text => {
                          try {
                            return JSON.parse(text);
                          } catch (e) {
                            console.error('Invalid JSON response:', text);
                            throw new Error('Invalid server response');
                          }
                        });
                      })
                      .then(data => {
                        if (data.message) {
                          setMessage('ADP document uploaded successfully');
                          (e.target as HTMLFormElement).reset();
                        } else {
                          setMessage('Failed to upload ADP document');
                        }
                      })
                      .catch(error => {
                        console.error('Upload error:', error);
                        setMessage(`Failed to upload ADP document: ${error.message}`);
                      });
                    }}>
                      <input name="file" type="file" accept=".pdf,.doc,.docx" required />
                      <textarea name="description" placeholder="ADP Description" rows={2} />
                      <button type="submit" className="btn btn-secondary" disabled={uploading}>{uploading ? '⏳ Uploading...' : 'Upload ADP'}</button>
                    </form>
                  </div>
                  <div className="doc-type">
                    <h4>Policy Documents</h4>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target as HTMLFormElement);
                      formData.append('document_type', 'policy');
                      formData.append('uploaded_by', user?.id || '');
                      
                      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                      
                      fetch(`${API_BASE}/admin/documents`, {
                        method: 'POST',
                        body: formData,
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
                        }
                      })
                      .then(response => {
                        if (!response.ok) {
                          throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.text().then(text => {
                          try {
                            return JSON.parse(text);
                          } catch (e) {
                            console.error('Invalid JSON response:', text);
                            throw new Error('Invalid server response');
                          }
                        });
                      })
                      .then(data => {
                        if (data.message) {
                          setMessage('Policy document uploaded successfully');
                          (e.target as HTMLFormElement).reset();
                        } else {
                          setMessage('Failed to upload policy document');
                        }
                      })
                      .catch(error => {
                        console.error('Upload error:', error);
                        setMessage(`Failed to upload policy document: ${error.message}`);
                      });
                    }}>
                      <input name="title" placeholder="Policy Document Title" required />
                      <input name="file" type="file" accept=".pdf,.doc,.docx" required />
                      <textarea name="description" placeholder="Policy Description" rows={2} />
                      <button type="submit" className="btn btn-secondary" disabled={uploading}>{uploading ? '⏳ Uploading...' : 'Upload Policy'}</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'project-allocations' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">🏗️</div>
              <div>
                <h2>Extract and Enter Project Allocations</h2>
                <p>Extract project allocations from budget documents and categorize by sector and ward.</p>
              </div>
            </div>
            
            <div className="project-form">
              <h3>Add Project Allocation</h3>
              <div className="example-box">
                <h4>Example Activity:</h4>
                <p><strong>Project:</strong> Kakuma Borehole Construction</p>
                <p><strong>Allocation:</strong> 45M KES</p>
                <p><strong>Sector:</strong> Water</p>
                <p><strong>Ward:</strong> Kakuma</p>
              </div>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                createProject();
              }}>
                <div className="form-row">
                  <input 
                    type="text" 
                    placeholder="Project Name" 
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    required 
                  />
                  <input 
                    type="number" 
                    placeholder="Budget Allocation (KES)" 
                    value={newProject.budget || ''}
                    onChange={(e) => setNewProject({...newProject, budget: parseInt(e.target.value) || 0})}
                    required 
                  />
                </div>
                <div className="form-row">
                  <select 
                    value={newProject.sector}
                    onChange={(e) => setNewProject({...newProject, sector: e.target.value})}
                    required
                  >
                    <option value="Health">Health</option>
                    <option value="Infrastructure">Infrastructure</option>
                    <option value="Water">Water</option>
                    <option value="Education">Education</option>
                    <option value="Agriculture">Agriculture</option>
                    <option value="Energy">Energy</option>
                    <option value="Other">Other</option>
                  </select>
                  <input 
                    type="text" 
                    placeholder="Ward ID" 
                    value={newProject.ward_id}
                    onChange={(e) => setNewProject({...newProject, ward_id: e.target.value})}
                    required 
                  />
                </div>
                <div className="form-row">
                  <input 
                    type="text" 
                    placeholder="Contractor (optional)" 
                    value={newProject.contractor}
                    onChange={(e) => setNewProject({...newProject, contractor: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <input 
                    type="date" 
                    placeholder="Start Date" 
                    value={newProject.start_date}
                    onChange={(e) => setNewProject({...newProject, start_date: e.target.value})}
                    required 
                  />
                  <input 
                    type="date" 
                    placeholder="Expected Completion" 
                    value={newProject.expected_completion}
                    onChange={(e) => setNewProject({...newProject, expected_completion: e.target.value})}
                    required 
                  />
                </div>
                <div className="form-row">
                  <textarea 
                    placeholder="Project Description" 
                    value={newProject.description}
                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                    rows={3}
                    required
                  />
                </div>
                <button type="submit">Submit Project Allocation</button>
              </form>
            </div>
          </section>
        )}

        {activeTab === 'ward-submissions' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📋</div>
              <div>
                <h2>Review Ward Submissions</h2>
                <p>Review and validate project submissions from civic moderators before sending to manager.</p>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading ward submissions...</div>
            ) : (
              <div className="submissions-list">
                {projects.length === 0 ? (
                  <div className="empty-state">
                    <p>No ward submissions pending review</p>
                  </div>
                ) : (
                  projects.map(project => (
                    <div key={project.id} className="submission-item">
                      <div className="submission-header">
                        <h3>{project.name}</h3>
                        <span className="submission-budget">KES {project.budget.toLocaleString()}</span>
                      </div>
                      <div className="submission-details">
                        <p><strong>Sector:</strong> {project.sector}</p>
                        <p><strong>Ward:</strong> {project.ward_id}</p>
                        <p><strong>Submitted by:</strong> {project.submitted_by_role}</p>
                        <p><strong>Description:</strong> {project.description}</p>
                      </div>
                      <div className="submission-actions">
                        <button 
                          onClick={() => validateProject(project.id, 'validate')}
                          className="approve-btn"
                        >
                          Validate & Forward to Manager
                        </button>
                        <button 
                          onClick={() => {
                            const notes = prompt('Correction notes:');
                            if (notes) validateProject(project.id, 'request_correction', notes);
                          }}
                          className="correction-btn"
                        >
                          Request Correction
                        </button>
                        <button 
                          onClick={() => {
                            const reason = prompt('Reason for rejection:');
                            if (reason) validateProject(project.id, 'reject', reason);
                          }}
                          className="reject-btn"
                        >
                          Reject Submission
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'county-issues' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">🚨</div>
              <div>
                <h2>County-Level Issues</h2>
                <p>Document, track and respond to issues flagged from wards and citizens.</p>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {[{label: 'Open', filter: 'open', color: '#e53935'}, {label: 'Under Review', filter: 'under_review', color: '#fb8c00'}, {label: 'Resolved', filter: 'resolved', color: '#43a047'}].map(s => (
                <div key={s.label} className="stat-card" style={{ flex: 1, minWidth: '120px', borderLeft: `4px solid ${s.color}` }}>
                  <h3 style={{ color: s.color }}>{issues.filter(i => i.status === s.filter).length}</h3>
                  <p>{s.label}</p>
                </div>
              ))}
              <div className="stat-card" style={{ flex: 1, minWidth: '120px' }}>
                <h3>{issues.filter(i => i.urgency === 'high').length}</h3>
                <p>High Priority</p>
              </div>
            </div>

            {/* Log New Issue */}
            <details style={{ marginBottom: '1.5rem', background: '#f9f9f9', borderRadius: '8px', padding: '1rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '1rem' }}>➕ Log New Issue</summary>
              <form style={{ marginTop: '1rem' }} onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target as HTMLFormElement);
                try {
                  await issuesAPI.create({
                    title: fd.get('title'),
                    description: fd.get('description'),
                    issue_type: fd.get('issue_type'),
                    urgency: fd.get('urgency'),
                    project_id: fd.get('project_id') || null,
                    flagged_by: user.id
                  });
                  setMessage('Issue logged successfully');
                  (e.target as HTMLFormElement).reset();
                  loadData();
                } catch { setMessage('Failed to log issue'); }
              }}>
                <div className="form-row">
                  <input name="title" placeholder="Issue title" required />
                  <select name="issue_type" required>
                    <option value="">Select Type</option>
                    <option value="project_delay">Project Delay</option>
                    <option value="budget_misuse">Budget Misuse</option>
                    <option value="contractor_issue">Contractor Issue</option>
                    <option value="safety_concern">Safety Concern</option>
                    <option value="community_complaint">Community Complaint</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-row">
                  <select name="urgency" required>
                    <option value="high">High Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="low">Low Priority</option>
                  </select>
                  <select name="project_id">
                    <option value="">Related Project (optional)</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <textarea name="description" placeholder="Detailed description of the issue" rows={3} required />
                </div>
                <button type="submit" className="btn btn-primary">Log Issue</button>
              </form>
            </details>

            {/* Issues List */}
            {loading ? <div className="loading">Loading issues...</div> : (
              issues.length === 0 ? (
                <div className="empty-state"><p>No issues logged yet.</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {issues.map(issue => (
                    <div key={issue.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.2rem', borderLeft: `4px solid ${issue.urgency === 'high' ? '#e53935' : issue.urgency === 'medium' ? '#fb8c00' : '#43a047'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div>
                          <h3 style={{ margin: 0 }}>{issue.title}</h3>
                          <small style={{ color: '#666' }}>
                            {issue.issue_type.replace(/_/g, ' ').toUpperCase()} &bull; Flagged by {issue.flagged_by_name} &bull; {new Date(issue.created_at).toLocaleDateString()}
                          </small>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', background: issue.status === 'open' ? '#ffebee' : issue.status === 'resolved' ? '#e8f5e9' : '#fff3e0', color: issue.status === 'open' ? '#e53935' : issue.status === 'resolved' ? '#43a047' : '#fb8c00', fontWeight: 600 }}>
                            {issue.status.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', background: '#f5f5f5' }}>
                            {issue.urgency.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      <p style={{ margin: '0.6rem 0', color: '#444' }}>{issue.description}</p>

                      {issue.project_name && <p style={{ margin: '0.3rem 0', fontSize: '0.85rem' }}>🏗️ <strong>Project:</strong> {issue.project_name}</p>}
                      {issue.ward_name && <p style={{ margin: '0.3rem 0', fontSize: '0.85rem' }}>📍 <strong>Ward:</strong> {issue.ward_name}</p>}

                      {issue.response && (
                        <div style={{ background: '#e8f5e9', borderRadius: '6px', padding: '0.8rem', marginTop: '0.8rem' }}>
                          <strong>✅ Response by {issue.responded_by_name}:</strong>
                          <p style={{ margin: '0.3rem 0 0' }}>{issue.response}</p>
                          <small style={{ color: '#666' }}>{issue.responded_at ? new Date(issue.responded_at).toLocaleString() : ''}</small>
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                        {issue.status !== 'resolved' && (
                          <>
                            {respondingTo === issue.id ? (
                              <div style={{ width: '100%' }}>
                                <textarea
                                  value={responseText}
                                  onChange={e => setResponseText(e.target.value)}
                                  placeholder="Type your response..."
                                  rows={3}
                                  style={{ width: '100%', marginBottom: '0.5rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button className="btn btn-primary" onClick={async () => {
                                    if (!responseText.trim()) return;
                                    try {
                                      await issuesAPI.update(issue.id, { response: responseText, responded_by: user.id });
                                      setMessage('Response submitted successfully');
                                      setRespondingTo(null);
                                      setResponseText('');
                                      loadData();
                                    } catch { setMessage('Failed to submit response'); }
                                  }}>Submit Response</button>
                                  <button className="btn btn-secondary" onClick={() => { setRespondingTo(null); setResponseText(''); }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <button className="btn btn-primary" onClick={() => { setRespondingTo(issue.id); setResponseText(''); }}>💬 Respond</button>
                            )}
                            <select
                              value={issue.status}
                              onChange={async (e) => {
                                try {
                                  await issuesAPI.update(issue.id, { status: e.target.value });
                                  setMessage('Status updated');
                                  loadData();
                                } catch { setMessage('Failed to update status'); }
                              }}
                              style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #ccc' }}
                            >
                              <option value="open">Open</option>
                              <option value="under_review">Under Review</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                          </>
                        )}
                        <button className="btn btn-danger" style={{ marginLeft: 'auto' }} onClick={async () => {
                          if (!window.confirm('Delete this issue?')) return;
                          try {
                            await issuesAPI.delete(issue.id);
                            setMessage('Issue deleted');
                            loadData();
                          } catch { setMessage('Failed to delete issue'); }
                        }}>🗑️ Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </section>
        )}

        {activeTab === 'community-engagement' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">🤝</div>
              <div>
                <h2>Community Engagement Documentation</h2>
                <p>Document community meetings, training sessions, and stakeholder interactions with photos, videos, and materials.</p>
              </div>
            </div>
            
            <div className="engagement-form">
              <h3>Record New Engagement</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                
                // Add user information
                formData.append('conducted_by', user?.id || '');
                formData.append('officer_role', 'county_officer');
                
                // Submit engagement
                const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                
                fetch(`${API_BASE}/admin/engagements`, {
                  method: 'POST',
                  body: formData
                })
                .then(response => response.json())
                .then(data => {
                  if (data.message) {
                    setMessage('Engagement documented successfully');
                    (e.target as HTMLFormElement).reset();
                  } else {
                    setMessage('Failed to document engagement');
                  }
                })
                .catch(error => {
                  console.error('Engagement error:', error);
                  setMessage('Failed to document engagement');
                });
              }}>
                <div className="form-row">
                  <input name="title" placeholder="Engagement Title" required />
                  <select name="engagement_type" required>
                    <option value="">Select Engagement Type</option>
                    <option value="community_meeting">Community Meeting</option>
                    <option value="stakeholder_meeting">Stakeholder Meeting</option>
                    <option value="training_session">Training Session</option>
                    <option value="site_visit">Site Visit</option>
                    <option value="public_hearing">Public Hearing</option>
                    <option value="consultation">Public Consultation</option>
                  </select>
                </div>
                
                <div className="form-row">
                  <input name="location" placeholder="Location/Venue" required />
                  <input name="participants_count" type="number" placeholder="Number of Participants" min="0" />
                </div>
                
                <div className="form-row">
                  <input name="engagement_date" type="datetime-local" required />
                  <input name="duration_hours" type="number" step="0.5" placeholder="Duration (hours)" min="0" />
                </div>
                
                <div className="form-row">
                  <select name="ward_id">
                    <option value="">Select Ward (if applicable)</option>
                    <option value="kakuma">Kakuma</option>
                    <option value="lodwar-township">Lodwar Township</option>
                    <option value="turkwel">Turkwel</option>
                  </select>
                  <select name="project_id">
                    <option value="">Related Project (optional)</option>
                  </select>
                </div>
                
                <div className="form-row">
                  <textarea name="description" placeholder="Engagement Description" rows={3} required />
                </div>
                
                <div className="form-row">
                  <textarea name="outcomes" placeholder="Key Outcomes and Decisions" rows={2} />
                </div>
                
                <div className="form-row">
                  <textarea name="challenges" placeholder="Challenges Encountered" rows={2} />
                </div>
                
                <div className="form-row">
                  <textarea name="recommendations" placeholder="Recommendations and Next Steps" rows={2} />
                </div>
                
                <div className="file-upload-section">
                  <h4>Upload Documentation</h4>
                  <div className="upload-types">
                    <div className="upload-type">
                      <label>📷 Photos (Team photos, venue, participants)</label>
                      <input name="attachments" type="file" multiple accept="image/*" />
                    </div>
                    <div className="upload-type">
                      <label>🎥 Videos (Testimonials, presentations)</label>
                      <input name="attachments" type="file" multiple accept="video/*" />
                    </div>
                    <div className="upload-type">
                      <label>📄 Documents (Training materials, presentations)</label>
                      <input name="attachments" type="file" multiple accept=".pdf,.doc,.docx,.ppt,.pptx" />
                    </div>
                    <div className="upload-type">
                      <label>🎤 Audio (Recordings, testimonials)</label>
                      <input name="attachments" type="file" multiple accept="audio/*" />
                    </div>
                  </div>
                  <small>Maximum file size: 50MB per file. Multiple files allowed.</small>
                </div>
                
                <button type="submit" className="btn btn-primary">Document Engagement</button>
              </form>
            </div>
            
            <div className="engagement-examples">
              <h3>Documentation Examples</h3>
              <div className="example-grid">
                <div className="example-item">
                  <h4>🏢 Community Meeting</h4>
                  <ul>
                    <li>Group photo with community members</li>
                    <li>Presentation slides or materials shared</li>
                    <li>Audio recording of key discussions</li>
                    <li>Feedback forms or notes</li>
                  </ul>
                </div>
                <div className="example-item">
                  <h4>🏭 Training Session</h4>
                  <ul>
                    <li>Training materials (PDFs, presentations)</li>
                    <li>Photos of training in progress</li>
                    <li>Video testimonials from participants</li>
                    <li>Certificates or completion documents</li>
                  </ul>
                </div>
                <div className="example-item">
                  <h4>🔍 Site Visit</h4>
                  <ul>
                    <li>Before/after photos of project site</li>
                    <li>Video walkthrough of progress</li>
                    <li>Technical documents or reports</li>
                    <li>Community feedback recordings</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'volunteer-activities' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">👥</div>
              <div>
                <h2>Community Volunteer Activities</h2>
                <p>View and monitor community volunteer engagements across all wards.</p>
              </div>
            </div>
            
            <div className="volunteer-filters">
              <div className="filter-row">
                <select id="volunteer-filter" defaultValue="">
                  <option value="">All Volunteers</option>
                  <option value="volunteer1">Mary Akiru (Kakuma)</option>
                  <option value="volunteer2">Peter Lokai (Lodwar)</option>
                  <option value="volunteer3">Grace Ekiru (Turkwel)</option>
                </select>
                <select id="ward-filter" defaultValue="">
                  <option value="">All Wards</option>
                  <option value="kakuma">Kakuma</option>
                  <option value="lodwar-township">Lodwar Township</option>
                  <option value="turkwel">Turkwel</option>
                </select>
                <select id="activity-type-filter" defaultValue="">
                  <option value="">All Activity Types</option>
                  <option value="community_meeting">Community Meetings</option>
                  <option value="training_session">Training Sessions</option>
                  <option value="material_distribution">Material Distribution</option>
                  <option value="feedback_collection">Feedback Collection</option>
                </select>
              </div>
            </div>
            
            <div className="volunteer-activities-list">
              <div className="activity-item">
                <div className="activity-header">
                  <div className="activity-info">
                    <h3>Health Education Session</h3>
                    <p><strong>Volunteer:</strong> Mary Akiru | <strong>Ward:</strong> Kakuma</p>
                  </div>
                  <div className="activity-meta">
                    <span className="activity-date">2 days ago</span>
                    <span className="activity-type">Training Session</span>
                  </div>
                </div>
                <div className="activity-details">
                  <p><strong>Location:</strong> Kakuma Community Center</p>
                  <p><strong>Participants:</strong> 25 community members</p>
                  <p><strong>Duration:</strong> 2 hours</p>
                  <p><strong>Description:</strong> Conducted health and hygiene awareness session focusing on water sanitation and disease prevention.</p>
                  <p><strong>Outcomes:</strong> Community members learned proper handwashing techniques and water treatment methods.</p>
                </div>
                <div className="activity-attachments">
                  <strong>Attachments:</strong>
                  <a href="#" className="attachment-link">📷 Group Photo</a>
                  <a href="#" className="attachment-link">📄 Training Materials</a>
                </div>
              </div>
              
              <div className="activity-item">
                <div className="activity-header">
                  <div className="activity-info">
                    <h3>Agricultural Guide Distribution</h3>
                    <p><strong>Volunteer:</strong> Peter Lokai | <strong>Ward:</strong> Lodwar Township</p>
                  </div>
                  <div className="activity-meta">
                    <span className="activity-date">1 week ago</span>
                    <span className="activity-type">Material Distribution</span>
                  </div>
                </div>
                <div className="activity-details">
                  <p><strong>Location:</strong> Lodwar Market</p>
                  <p><strong>Participants:</strong> 30 farmers</p>
                  <p><strong>Duration:</strong> 1.5 hours</p>
                  <p><strong>Description:</strong> Distributed farming best practices guides and provided consultation on drought-resistant crops.</p>
                  <p><strong>Outcomes:</strong> Farmers received practical guidance on improving crop yields during dry seasons.</p>
                </div>
                <div className="activity-attachments">
                  <strong>Attachments:</strong>
                  <a href="#" className="attachment-link">📷 Distribution Photos</a>
                  <a href="#" className="attachment-link">📄 Agricultural Guides</a>
                  <a href="#" className="attachment-link">🎥 Farmer Testimonials</a>
                </div>
              </div>
              
              <div className="activity-item">
                <div className="activity-header">
                  <div className="activity-info">
                    <h3>Community Feedback Collection</h3>
                    <p><strong>Volunteer:</strong> Grace Ekiru | <strong>Ward:</strong> Turkwel</p>
                  </div>
                  <div className="activity-meta">
                    <span className="activity-date">2 weeks ago</span>
                    <span className="activity-type">Feedback Collection</span>
                  </div>
                </div>
                <div className="activity-details">
                  <p><strong>Location:</strong> Various locations in Turkwel</p>
                  <p><strong>Participants:</strong> 15 community members</p>
                  <p><strong>Duration:</strong> 4 hours</p>
                  <p><strong>Description:</strong> Collected community feedback on water project progress and identified additional needs.</p>
                  <p><strong>Outcomes:</strong> Gathered 15 feedback items highlighting both satisfaction and areas for improvement.</p>
                </div>
                <div className="activity-attachments">
                  <strong>Attachments:</strong>
                  <a href="#" className="attachment-link">📷 Site Photos</a>
                  <a href="#" className="attachment-link">🎤 Audio Feedback</a>
                  <a href="#" className="attachment-link">📄 Feedback Summary</a>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'document-management' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📁</div>
              <div>
                <h2>Document Management</h2>
                <p>View, update, and delete uploaded documents.</p>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading documents...</div>
            ) : (
              <div className="documents-list">
                {documents.length === 0 ? (
                  <div className="empty-state">
                    <p>No documents found</p>
                  </div>
                ) : (
                  <div className="documents-grid">
                    {documents.map(doc => (
                      <div key={doc.id} className="document-card">
                        {editingDoc?.id === doc.id ? (
                          <div className="edit-form">
                            <input type="text" value={editingDoc.title} onChange={(e) => setEditingDoc({...editingDoc, title: e.target.value})} placeholder="Title" />
                            <textarea value={editingDoc.description || ''} onChange={(e) => setEditingDoc({...editingDoc, description: e.target.value})} placeholder="Description" rows={3} />
                            <select value={editingDoc.type} onChange={(e) => setEditingDoc({...editingDoc, type: e.target.value})}>
                              <option value="budget">Budget</option>
                              <option value="plan">Plan</option>
                              <option value="policy">Policy</option>
                              <option value="report">Report</option>
                            </select>
                            <div className="edit-actions">
                              <button onClick={() => updateDocument(doc.id, { title: editingDoc.title, description: editingDoc.description, document_type: editingDoc.type })} className="btn btn-primary">Save</button>
                              <button onClick={() => setEditingDoc(null)} className="btn btn-secondary">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="doc-header">
                              <h3>{doc.title}</h3>
                              <span className="doc-type">{doc.type?.toUpperCase()}</span>
                            </div>
                            <div className="doc-details">
                              <p><strong>Uploaded by:</strong> {doc.uploaded_by || 'Unknown'}</p>
                              <p><strong>Description:</strong> {doc.description || 'No description'}</p>
                              <p><strong>Sector:</strong> {doc.sector || 'N/A'}</p>
                              <p><strong>Ward:</strong> {doc.ward || 'County-wide'}</p>
                              <p><strong>Format:</strong> {doc.file_format}</p>
                              <p><strong>Size:</strong> {doc.file_size > 0 ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : <span style={{ color: '#e53935' }}>⚠️ No file — re-upload needed</span>}</p>
                              <p><strong>Status:</strong> <span style={{ color: doc.approval_status === 'approved' ? '#43a047' : '#fb8c00' }}>{doc.approval_status}</span></p>
                              <p><strong>Uploaded:</strong> {new Date(doc.created_at).toLocaleDateString()}</p>
                            </div>
                            <div className="doc-actions">
                              {doc.file_size > 0 && (
                                <a href={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">📥 Download</a>
                              )}
                              <button onClick={() => setEditingDoc(doc)} className="btn btn-primary">✏️ Edit</button>
                              <button onClick={() => deleteDocument(doc.id)} className="btn btn-danger">🗑️ Delete</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'feedback-review' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📝</div>
              <div>
                <h2>Feedback Review</h2>
                <p>Review and moderate feedback submitted by citizens, volunteers, and community members.</p>
              </div>
            </div>
            {loading ? <div className="loading">Loading feedback...</div> : (
              <div className="feedback-list">
                {feedback.length === 0 ? (
                  <div className="empty-state"><p>No feedback submissions found.</p></div>
                ) : (
                  feedback.map((item: any) => (
                    <div key={item.id} className="feedback-item">
                      <div className="feedback-header">
                        <div className="feedback-info">
                          <strong>{item.is_anonymous ? 'Anonymous' : item.name}</strong>
                          <span className="feedback-type">{item.feedback_type}</span>
                          <span className={`status-badge ${item.status}`}>{item.status}</span>
                          {item.is_public && <span style={{ fontSize: '0.75rem', color: '#43a047', marginLeft: '0.4rem' }}>🌐 Public</span>}
                        </div>
                        <div className="feedback-date">{new Date(item.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="feedback-details">
                        <p><strong>County:</strong> {item.county}{item.ward ? ` · ${item.ward}` : ''}</p>
                        <p><strong>Message:</strong> {item.message}</p>
                      </div>
                      <div className="feedback-actions">
                        <button onClick={() => feedbackAPI.moderate(item.id, { status: 'verified', is_public: true }).then(() => loadData())} className="approve-btn">✅ Approve & Publish</button>
                        <button onClick={() => feedbackAPI.moderate(item.id, { status: 'verified', is_public: false }).then(() => loadData())} className="approve-private-btn">🔒 Approve (Private)</button>
                        <button onClick={() => feedbackAPI.moderate(item.id, { status: 'rejected', is_public: false }).then(() => loadData())} className="reject-btn">❌ Reject</button>
                        <button onClick={() => { if (window.confirm('Delete this feedback?')) feedbackAPI.delete(item.id).then(() => loadData()); }} className="btn btn-danger" style={{ marginLeft: 'auto' }}>🗑️ Delete</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'ward-approvals' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">✅</div>
              <div>
                <h2>Approve Civic Moderator Documents</h2>
                <p>Review and approve documents uploaded by civic moderators before they become public.</p>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading pending documents...</div>
            ) : (
              <div className="documents-list">
                {pendingDocuments.length === 0 ? (
                  <div className="empty-state">
                    <p>No pending ward documents to review</p>
                  </div>
                ) : (
                  <div className="documents-grid">
                    {pendingDocuments.map(doc => (
                      <div key={doc.id} className="document-card">
                        <div className="doc-header">
                          <h3>{doc.title}</h3>
                          <span className="doc-type">{doc.document_type}</span>
                        </div>
                        <div className="doc-details">
                          <p><strong>Uploaded by:</strong> {doc.uploader_name} ({doc.uploader_role})</p>
                          <p><strong>Ward:</strong> {doc.ward_name || 'County-wide'}</p>
                          <p><strong>Sector:</strong> {doc.sector || 'N/A'}</p>
                          <p><strong>Description:</strong> {doc.description || 'No description'}</p>
                          <p><strong>Format:</strong> {doc.file_format}</p>
                          <p><strong>Size:</strong> {(doc.file_size / 1024 / 1024).toFixed(2)} MB</p>
                          <p><strong>Uploaded:</strong> {new Date(doc.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="doc-actions">
                          <a 
                            href={`${process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000'}${doc.file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary"
                          >
                            👁️ Preview
                          </a>
                          <button 
                            onClick={() => {
                              if (window.confirm('Approve this document? It will become publicly visible.')) {
                                approveWardDocument(doc.id, 'approve');
                              }
                            }}
                            className="btn btn-success"
                          >
                            ✓ Approve
                          </button>
                          <button 
                            onClick={() => {
                              const reason = prompt('Enter rejection reason:');
                              if (reason) {
                                approveWardDocument(doc.id, 'reject', reason);
                              }
                            }}
                            className="btn btn-danger"
                          >
                            ✗ Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="volunteer-info">
              <h3>Document Approval Guidelines</h3>
              <p>When reviewing civic moderator documents, verify:</p>
              <ul>
                <li>✓ Document is relevant to ward activities</li>
                <li>✓ Content is accurate and appropriate</li>
                <li>✓ File format and quality are acceptable</li>
                <li>✓ No sensitive or confidential information exposed</li>
                <li>✓ Aligns with county policies and standards</li>
              </ul>
              
              <h4>What Happens After Approval?</h4>
              <ul>
                <li>Document becomes publicly visible on the Documents page</li>
                <li>Citizens can view and download the document</li>
                <li>Contributes to county transparency metrics</li>
                <li>Civic moderator receives confirmation of approval</li>
              </ul>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default CountyOfficerPage;